/**
 * printerService.ts
 *
 * Capa unificada de impresión. Detecta plataforma y enruta al backend correcto.
 *
 * Backends soportados:
 *   sunmi-native   — impresora integrada SUNMI (solo APK Android)
 *   web-bluetooth  — Web Bluetooth API (Chrome/Edge)
 *   browser-print  — window.print() popup HTML (fallback universal)
 */

import { isWebBluetoothAvailable, isSunmiCandidate } from '../platform';

export type PrintMethod = 'sunmi-native' | 'web-bluetooth' | 'browser-print';

const LS_PREFERRED_METHOD_KEY = 'tiempos_print_method';

/** Todos los métodos disponibles en este entorno, en orden de prioridad. */
export function getAvailablePrintMethods(): PrintMethod[] {
  const methods: PrintMethod[] = [];
  if (isSunmiCandidate()) methods.push('sunmi-native');
  if (isWebBluetoothAvailable()) methods.push('web-bluetooth');
  methods.push('browser-print');
  return methods;
}

/** Mejor método disponible. SUNMI tiene prioridad si está disponible. */
export function detectRecommendedMethod(): PrintMethod {
  // SUNMI siempre tiene prioridad absoluta en APK
  // Ignora localStorage para evitar conflictos con preferencias guardadas en web
  if (isSunmiCandidate()) {
    return 'sunmi-native';
  }

  const saved = localStorage.getItem(LS_PREFERRED_METHOD_KEY) as PrintMethod | null;
  const available = getAvailablePrintMethods();
  if (saved && available.includes(saved)) return saved;
  return available[0];
}

/** Conectar impresora según método. Guarda preferencia. */
export async function connectPrinter(
  method?: PrintMethod,
): Promise<{ name: string; method: PrintMethod }> {
  const m = method || detectRecommendedMethod();

  if (m === 'sunmi-native') {
    // En SUNMI, no bloqueamos con isSunmiAvailable().
    // Si somos candidato SUNMI (Android nativo), asumimos que está disponible.
    // printViaSunmi() manejará errores si el servicio no está listo.
    if (isSunmiCandidate()) {
      localStorage.setItem(LS_PREFERRED_METHOD_KEY, 'sunmi-native');
      return { name: 'SUNMI Integrada', method: 'sunmi-native' };
    }
    // Si no es candidato SUNMI, fallback
    localStorage.removeItem(LS_PREFERRED_METHOD_KEY);
    return { name: 'Navegador', method: 'browser-print' };
  }

  if (m === 'web-bluetooth') {
    const { connectBluetoothPrinter } = await import('./escposPrinter');
    const printer = await connectBluetoothPrinter();
    localStorage.setItem(LS_PREFERRED_METHOD_KEY, 'web-bluetooth');
    return { name: printer.name, method: 'web-bluetooth' };
  }

  // browser-print no requiere conexión
  return { name: 'Navegador', method: 'browser-print' };
}

/**
 * Imprimir texto vía el backend adecuado.
 * Para browser-print, el caller maneja el fallback con window.print() / imprimirTicket().
 */
export async function printTicketText(
  ticketText: string,
  options?: { ancho?: 58 | 80 },
): Promise<void> {
  const method = detectRecommendedMethod();

  if (method === 'sunmi-native') {
    try {
      const { printViaSunmi } = await import('./sunmiPrinter');
      const result = await printViaSunmi(ticketText);
      if (result.success) return;
      throw new Error(result.error || 'SUNMI printText falló');
    } catch (e) {
      console.error('[printerService] SUNMI print falló:', e);
      throw e;
    }
  }

  if (method === 'web-bluetooth') {
    const { printTicketBluetooth } = await import('./escposPrinter');
    await printTicketBluetooth(ticketText, options);
    return;
  }

  throw new Error('No hay método de impresión Bluetooth disponible.');
}

/**
 * Interfaz unificada de impresión con fallback automático.
 * Intenta SUNMI → Bluetooth → lanza error (caller usa window.print).
 */
export async function print(
  text: string,
  options?: { type?: PrintMethod; ancho?: 58 | 80 },
): Promise<void> {
  const method = options?.type || detectRecommendedMethod();

  if (method === 'sunmi-native') {
    try {
      const { printViaSunmi } = await import('./sunmiPrinter');
      const result = await printViaSunmi(text);
      if (result.success) return;
      console.warn('[printerService] SUNMI falló, intentando fallback:', result.error);
    } catch (e) {
      console.warn('[printerService] SUNMI exception, intentando fallback:', e);
    }

    // Fallback a Bluetooth si está disponible
    if (isWebBluetoothAvailable()) {
      try {
        const { printTicketBluetooth } = await import('./escposPrinter');
        await printTicketBluetooth(text, { ancho: options?.ancho });
        return;
      } catch (btErr) {
        console.warn('[printerService] Bluetooth fallback falló:', btErr);
      }
    }

    throw new Error('Impresión SUNMI falló y no hay fallback disponible.');
  }

  if (method === 'web-bluetooth') {
    const { printTicketBluetooth } = await import('./escposPrinter');
    await printTicketBluetooth(text, { ancho: options?.ancho });
    return;
  }

  throw new Error('Método browser-print requiere HTML — usar imprimirTicket() directamente.');
}
